// Pure detail <-> form <-> request mapping for the reservation form, kept out of the component so it
// holds only wiring. Mirrors water-information.mapper.ts (patchForms/toSaveDto).
import { FormGroup } from '@angular/forms';
import {
  CoTravelerRequest,
  ReservationAccommodationRequest,
  ReservationCreateOrUpdateRequest,
  ReservationDetail,
  ReservationFishingSpotRequest,
} from '@models/reservations';
import { PickedCustomer } from '@utils/customer-autocomplete';
import { toDateOnly } from '@utils/date-only';
import { ReservationForm } from './reservation-form.model';

type ReservationFormValue = ReturnType<FormGroup<ReservationForm>['getRawValue']>;
type ReservationPatch = Parameters<FormGroup<ReservationForm>['patchValue']>[0];

// Seed values for one co-traveler row — the args createCoTravelerFormGroup consumes.
interface CoTravelerSeed {
  id: number | null;
  customer: PickedCustomer;
  periodTargetId: number | null;
}

// Seed values for one accommodation row — the args createAccommodationFormGroup consumes.
interface AccommodationSeed {
  id: number | null;
  accommodationId: number;
  fisherMan: number;
  nonFisherMan: number;
  arrivalDate: Date | null;
  departureDate: Date | null;
}

// Seed values for one fishing-spot row — the args createFishingSpotFormGroup consumes.
interface FishingSpotSeed {
  id: number | null;
  fishingSpotId: number | null;
  fisherMan: number;
  nonFisherMan: number;
  arrivalDate: Date | null;
  departureDate: Date | null;
  wholeWater: boolean;
}

// Scalar fields for form.patchValue(). The row arrays are rebuilt separately (see the seeds
// below) because their factories attach per-row search subscriptions owned by the component.
export function toReservationPatch(r: ReservationDetail): ReservationPatch {
  return {
    // Seed the autocomplete with the existing booker as a picked object so the control stays valid.
    customer: r.customerId != null ? { id: r.customerId, label: r.customerName ?? '' } : null,
    createdOn: r.createdOn ? new Date(r.createdOn) : null,
    siteId: r.siteId,
    waterId: r.waterId,
    statusId: r.statusId,
    reservationType: r.reservationType,
    companyName: r.companyName,
    lastMinute: r.lastMinute,
    insurance: r.insurance,
    dog: r.dog,
    dogBreed: r.dogBreed,
    financial: r.financial,
    retailPrice: r.retailPrice,
    purchasePrice: r.purchasePrice,
    emergencyContact: r.emergencyContact,
    reservationComment: r.reservationComment,
  };
}

// Co-traveler rows to rebuild — each customer seeded as a picked object so requireSelection stays satisfied.
export function toCoTravelerSeeds(r: ReservationDetail): CoTravelerSeed[] {
  return (r.coTravelers ?? []).map((ct) => ({
    id: ct.id,
    customer: { id: ct.customerId, label: ct.customerName ?? '' },
    periodTargetId: ct.periodTargetId,
  }));
}

// Accommodation rows to rebuild from the persisted assignments.
export function toAccommodationSeeds(r: ReservationDetail): AccommodationSeed[] {
  return (r.accommodations ?? []).map((a) => ({
    id: a.id,
    accommodationId: a.accommodationId,
    fisherMan: a.fisherMan,
    nonFisherMan: a.nonFisherMan,
    arrivalDate: a.arrivalDate ? new Date(a.arrivalDate) : null,
    departureDate: a.departureDate ? new Date(a.departureDate) : null,
  }));
}

// Fishing-spot ("swim") rows to rebuild from the persisted assignments.
export function toFishingSpotSeeds(r: ReservationDetail): FishingSpotSeed[] {
  return (r.fishingSpots ?? []).map((fs) => ({
    id: fs.id,
    fishingSpotId: fs.fishingSpotId,
    fisherMan: fs.fisherMan,
    nonFisherMan: fs.nonFisherMan,
    arrivalDate: fs.arrivalDate ? new Date(fs.arrivalDate) : null,
    departureDate: fs.departureDate ? new Date(fs.departureDate) : null,
    wholeWater: fs.wholeWater,
  }));
}

// Builds the create/update payload from the raw form value. Returns null if the required
// top-level ids are missing (the form's validators normally guarantee they aren't).
export function buildSaveRequest(v: ReservationFormValue): ReservationCreateOrUpdateRequest | null {
  // requireSelection guarantees `customer` is an object here, but narrow for the type system.
  const customerId = typeof v.customer === 'object' && v.customer ? v.customer.id : null;
  if (customerId == null || v.siteId == null || v.waterId == null || v.statusId == null) return null;

  // requireSelection on each row guarantees a picked customer; narrow + drop any stragglers.
  const coTravelers: CoTravelerRequest[] = v.coTravelers
    .map((row) => ({
      id: row.id,
      customerId: typeof row.customer === 'object' && row.customer ? row.customer.id : null,
      periodTargetId: row.periodTargetId,
    }))
    .filter((row): row is CoTravelerRequest => row.customerId != null);

  // Validators guarantee an accommodation id and both dates on each row; narrow + drop any stragglers.
  const accommodations: ReservationAccommodationRequest[] = v.accommodations
    .map((row) => ({
      id: row.id,
      accommodationId: row.accommodationId,
      arrivalDate: toDateOnly(row.arrivalDate),
      departureDate: toDateOnly(row.departureDate),
      fisherMan: row.fisherMan,
      nonFisherMan: row.nonFisherMan,
    }))
    .filter(
      (row): row is ReservationAccommodationRequest =>
        row.accommodationId != null &&
        row.arrivalDate != null &&
        row.departureDate != null &&
        row.fisherMan != null &&
        row.nonFisherMan != null,
    );

  // Validators guarantee both dates and counts on each row; narrow + drop any stragglers. The spot id
  // is intentionally kept even when null — an unset/dummy swim (whole-water booking or a water with no
  // selectable swims) is a valid row, mirroring the legacy backend.
  const fishingSpots: ReservationFishingSpotRequest[] = v.fishingSpots
    .map((row) => ({
      id: row.id,
      fishingSpotId: row.fishingSpotId,
      arrivalDate: toDateOnly(row.arrivalDate),
      departureDate: toDateOnly(row.departureDate),
      fisherMan: row.fisherMan,
      nonFisherMan: row.nonFisherMan,
      wholeWater: row.wholeWater,
    }))
    .filter(
      (row): row is ReservationFishingSpotRequest =>
        row.arrivalDate != null && row.departureDate != null && row.fisherMan != null && row.nonFisherMan != null,
    );

  return {
    customerId,
    createdOn: toDateOnly(v.createdOn),
    siteId: v.siteId,
    waterId: v.waterId,
    statusId: v.statusId,
    reservationType: v.reservationType,
    companyName: v.companyName,
    lastMinute: v.lastMinute,
    insurance: v.insurance,
    dog: v.dog,
    dogBreed: v.dogBreed,
    financial: v.financial,
    retailPrice: v.retailPrice,
    purchasePrice: v.purchasePrice,
    emergencyContact: v.emergencyContact,
    reservationComment: v.reservationComment,
    coTravelers,
    accommodations,
    fishingSpots,
  };
}
